import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { FileText, Upload, Trash2, CheckCircle, Clock, HelpCircle, Eye, Save, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Popover from '../components/Popover';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import AppHeader from '../components/AppHeader';
import Breadcrumb from '../components/Breadcrumb';
import {
  PageContainer, PageHeader, PageTitle, PageSubtitle, Card, Badge, Button,
  Spinner, EmptyState, EmptyIcon, EmptyText, EmptyHint, Flex, Select, SectionTitle
} from '../components/ui';
import { theme } from '../styles/theme';
import api from '../services/api';

const KnowledgeBase = () => {
  const { t } = useTranslation('knowledge');
  const toast   = useToast();
  const confirm = useConfirm();
  const { tenantId } = useParams();
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');  // '' = todos os agentes
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorDoc, setEditorDoc] = useState(null);
  const [editorContent, setEditorContent] = useState('');
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorSaving, setEditorSaving] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSallusAdmin = user.role === 'sallus_admin';

  const breadcrumbItems = [
    isSallusAdmin
      ? { label: 'Libben Agents', path: '/sallus/dashboard' }
      : user.role === 'manager'
        ? { label: t('home', { ns: 'common' }), path: `/manager/${tenantId}` }
        : { label: t('home', { ns: 'common' }), path: `/dashboard/${tenantId}` },
    { label: t('title') }
  ];

  useEffect(() => {
    loadDocuments();
    loadAgents();
  }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Polling enquanto houver documentos processando
  useEffect(() => {
    const pending = documents.some(d => d.status !== 'processed');
    if (!pending) return;
    const timer = setInterval(loadDocuments, 5000);
    return () => clearInterval(timer);
  }, [documents]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDocuments = async () => {
    try {
      const res = await api.get(`/knowledge/${tenantId}/documents`);
      setDocuments(res.data.documents || []);
    } catch (e) { console.error(e); }
  };

  const loadAgents = async () => {
    try {
      const res = await api.get(`/marketplace/tenants/${tenantId}/agents`);
      setAgents(res.data.agents || []);
    } catch (e) { console.error(e); }
  };

  const handleDrag = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleFiles(e.dataTransfer.files);
  };

  const MIME_MAP = {
    'text/plain': 'text/plain',
    'text/markdown': 'text/markdown',
    'text/csv': 'text/csv',
    'text/html': 'text/html',
  };
  const ALLOWED_EXT = ['.txt', '.md', '.csv', '.html'];

  const resolveContentType = (file) => {
    if (file.type && MIME_MAP[file.type]) return file.type;
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (ext === '.txt')  return 'text/plain';
    if (ext === '.md')   return 'text/markdown';
    if (ext === '.csv')  return 'text/csv';
    if (ext === '.html') return 'text/html';
    return file.type || 'application/octet-stream';
  };

  const handleFiles = async (files) => {
    setUploading(true);
    try {
      const file = files[0];
      const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
      if (!ALLOWED_EXT.includes(ext)) {
        toast.error(t('invalidFileType', { ext, allowed: ALLOWED_EXT.join(', ') }));
        return;
      }
      const contentType = resolveContentType(file);
      const body = { name: file.name, size: file.size, contentType };
      if (selectedAgent) body.agentInstanceId = selectedAgent;
      const res = await api.post(`/knowledge/${tenantId}/documents`, body);
      const { document: doc, uploadUrl } = res.data;
      await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': contentType } });
      setDocuments(prev => [doc, ...prev]);
    } catch (e) {
      toast.error(t('uploadError', { msg: e.response?.data?.message || e.message }));
    } finally {
      setUploading(false);
    }
  };

  const deleteDocument = async (docId) => {
    const ok = await confirm({ title: t('deleteConfirm'), danger: true });
    if (!ok) return;
    try {
      await api.delete(`/knowledge/${tenantId}/documents/${docId}`);
      setDocuments(documents.filter(d => d.docId !== docId));
      toast.success(t('deleteSuccess', { ns: 'common' }));
    } catch (e) {
      toast.error(t('deleteError', { msg: e.response?.data?.message || e.message }));
    }
  };

  const openEditor = async (doc) => {
    setEditorDoc(doc);
    setEditorOpen(true);
    setEditorLoading(true);
    try {
      const res = await api.get(`/knowledge/${tenantId}/documents/${doc.docId || doc.id}/content`);
      setEditorContent(res.data.content || '');
    } catch (e) {
      toast.error(t('contentLoadError', { defaultValue: 'Erro ao carregar conteúdo' }));
      setEditorOpen(false);
    } finally {
      setEditorLoading(false);
    }
  };

  const saveContent = async () => {
    if (!editorDoc) return;
    setEditorSaving(true);
    try {
      const docId = editorDoc.docId || editorDoc.id;
      await api.put(`/knowledge/${tenantId}/documents/${docId}/content`, { content: editorContent });
      toast.success(t('contentSaved', { defaultValue: 'Conteúdo salvo e re-indexado' }));
      setEditorOpen(false);
      loadDocuments();
    } catch (e) {
      toast.error(t('contentSaveError', { defaultValue: 'Erro ao salvar conteúdo' }));
    } finally {
      setEditorSaving(false);
    }
  };

  return (
    <>
      <AppHeader title={t('title')} />
      <BreadcrumbBar><Breadcrumb items={breadcrumbItems} /></BreadcrumbBar>
      <PageContainer>
        <PageHeader>
          <div>
            <PageTitle>{t('title')}</PageTitle>
            <PageSubtitle>{t('subtitle')}</PageSubtitle>
          </div>
        </PageHeader>

        <Card style={{ marginBottom: theme.spacing[4] }}>
          <AgentScopeRow>
            <AgentScopeLabel>
              {t('scopeLabel')}
              <Popover content={t('tooltips.scope')} placement="right">
                <HelpCircle size={12} color={theme.colors.textMuted} style={{ marginLeft: 4, cursor: 'help' }} />
              </Popover>
            </AgentScopeLabel>
            <Select
              value={selectedAgent}
              onChange={e => setSelectedAgent(e.target.value)}
              style={{ minWidth: 220 }}
            >
              <option value="">{t('scopeAll')}</option>
              {agents.map(a => (
                <option key={a.instanceId} value={a.instanceId}>{a.name || a.instanceId}</option>
              ))}
            </Select>
          </AgentScopeRow>
        </Card>

        <DropZone active={dragActive} onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}>
          <Upload size={40} color={dragActive ? theme.colors.primary : theme.colors.gray300} />
          <DropTitle>{t('dropTitle')}</DropTitle>
          <DropSub>{t('dropOr')}</DropSub>
          <Button as="label" variant="primary">
            {t('selectFile')}
            <input type="file" accept=".txt,.md,.csv,.html" onChange={e => e.target.files?.[0] && handleFiles(e.target.files)} style={{ display: 'none' }} />
          </Button>
          <DropHint>
            {t('dropHint')}
            <Popover content={t('tooltips.dropZone')} placement="bottom">
              <HelpCircle size={12} color={theme.colors.textMuted} style={{ marginLeft: 4, cursor: 'help' }} />
            </Popover>
          </DropHint>
        </DropZone>

        {uploading && (
          <Card style={{ marginBottom: theme.spacing[4] }}>
            <Flex gap={theme.spacing[3]}>
              <Spinner size="20px" />
              <span style={{ fontSize: theme.typography.sm, color: theme.colors.textSecondary }}>{t('processing')}</span>
            </Flex>
          </Card>
        )}

        <Card>
          <SectionTitle>{t('documents', { count: documents.length })}</SectionTitle>
          {documents.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FileText size={48} /></EmptyIcon>
              <EmptyText>{t('noDocuments')}</EmptyText>
              <EmptyHint>{t('noDocumentsHint', { defaultValue: 'Adicione arquivos de texto (.txt, .md, .csv, .html) para que seu assistente saiba mais sobre você e seu trabalho.' })}</EmptyHint>
            </EmptyState>
          ) : (
            <DocList>
              {documents.map(doc => (
                <DocRow key={doc.docId || doc.id}>
                  <FileText size={28} color={theme.colors.primary} style={{ flexShrink: 0 }} />
                  <DocInfo>
                    <DocName>{doc.name}</DocName>
                    <DocMeta>
                      {typeof doc.size === 'number' ? `${(doc.size / 1024 / 1024).toFixed(2)} MB` : doc.size}
                      {doc.pages > 0 && ` · ${t('pages', { count: doc.pages })}`}
                      {doc.uploadedAt && ` · ${doc.uploadedAt}`}
                      {doc.agentInstanceId && (
                        <AgentBadge>
                          {agents.find(a => a.instanceId === doc.agentInstanceId)?.name || doc.agentInstanceId}
                        </AgentBadge>
                      )}
                    </DocMeta>
                  </DocInfo>
                  <Popover content={t('tooltips.processingStatus')}>
                    <Badge variant={doc.status === 'processed' || doc.status === 'indexed' ? 'success' : 'warning'}>
                      {doc.status === 'processed' || doc.status === 'indexed'
                        ? <><CheckCircle size={10} /> {t('processed')}</>
                        : <><Clock size={10} /> {t('processing2')}</>}
                    </Badge>
                  </Popover>
                  <Button variant="ghost" size="sm" onClick={() => openEditor(doc)} title={t('viewEdit', { defaultValue: 'Ver / Editar' })}>
                    <Eye size={14} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteDocument(doc.docId || doc.id)}>
                    <Trash2 size={14} />
                  </Button>
                </DocRow>
              ))}
            </DocList>
          )}
        </Card>

        {editorOpen && (
          <EditorOverlay onClick={() => !editorSaving && setEditorOpen(false)}>
            <EditorModal onClick={e => e.stopPropagation()}>
              <EditorHeader>
                <EditorTitle>
                  <FileText size={18} color={theme.colors.primary} />
                  {editorDoc?.name}
                </EditorTitle>
                <Flex gap={theme.spacing[2]}>
                  <Button variant="primary" size="sm" onClick={saveContent} disabled={editorLoading || editorSaving}>
                    {editorSaving ? <Spinner size="14px" /> : <Save size={14} />}
                    {t('save', { ns: 'common', defaultValue: 'Salvar' })}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setEditorOpen(false)} disabled={editorSaving}>
                    <X size={14} />
                  </Button>
                </Flex>
              </EditorHeader>
              {editorLoading ? (
                <EditorLoadingState>
                  <Spinner size="24px" />
                  <span>{t('loadingContent', { defaultValue: 'Carregando conteúdo...' })}</span>
                </EditorLoadingState>
              ) : (
                <EditorTextarea
                  value={editorContent}
                  onChange={e => setEditorContent(e.target.value)}
                  placeholder={t('editorPlaceholder', { defaultValue: 'Conteúdo do documento...' })}
                  spellCheck={false}
                />
              )}
            </EditorModal>
          </EditorOverlay>
        )}
      </PageContainer>
    </>
  );
};

const BreadcrumbBar = styled.div`background: ${theme.colors.white}; padding: 0 ${theme.spacing[6]}; border-bottom: 1px solid ${theme.colors.border};`;
const DropZone = styled.div`background: ${theme.colors.white}; border: 2px dashed ${p => p.active ? theme.colors.primary : theme.colors.border}; border-radius: ${theme.radii.xl}; padding: ${theme.spacing[12]} ${theme.spacing[6]}; text-align: center; margin-bottom: ${theme.spacing[5]}; transition: ${theme.transitions.fast}; background: ${p => p.active ? theme.colors.primaryLight : theme.colors.white}; display: flex; flex-direction: column; align-items: center; gap: ${theme.spacing[3]};`;
const DropTitle = styled.div`font-size: ${theme.typography.lg}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary};`;
const DropSub = styled.div`font-size: ${theme.typography.sm}; color: ${theme.colors.textMuted};`;
const DropHint = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted};`;
// SectionTitle — importado de ui.js
const DocList = styled.div`display: flex; flex-direction: column; gap: ${theme.spacing[3]};`;
const DocRow = styled.div`display: flex; align-items: center; gap: ${theme.spacing[4]}; padding: ${theme.spacing[3]} ${theme.spacing[4]}; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.lg}; transition: ${theme.transitions.fast}; &:hover { border-color: ${theme.colors.primary}; background: ${theme.colors.primaryLight}; }`;
const DocInfo = styled.div`flex: 1; min-width: 0;`;
const DocName = styled.div`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.medium}; color: ${theme.colors.textPrimary}; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`;
const DocMeta = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; margin-top: 2px; display: flex; align-items: center; gap: ${theme.spacing[2]};`;
const AgentScopeRow = styled.div`display: flex; align-items: center; gap: ${theme.spacing[3]};`;
const AgentScopeLabel = styled.label`font-size: ${theme.typography.sm}; color: ${theme.colors.textSecondary}; display: flex; align-items: center;`;
const AgentBadge = styled.span`background: ${theme.colors.primaryLight}; color: ${theme.colors.primary}; border-radius: ${theme.radii.full}; padding: 1px 8px; font-size: ${theme.typography.xs}; font-weight: ${theme.typography.medium};`;
const EditorOverlay = styled.div`position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: ${theme.spacing[6]};`;
const EditorModal = styled.div`background: ${theme.colors.white}; border-radius: ${theme.radii.xl}; width: 100%; max-width: 900px; height: 80vh; display: flex; flex-direction: column; box-shadow: 0 20px 60px rgba(0,0,0,0.3);`;
const EditorHeader = styled.div`display: flex; align-items: center; justify-content: space-between; padding: ${theme.spacing[4]} ${theme.spacing[5]}; border-bottom: 1px solid ${theme.colors.border};`;
const EditorTitle = styled.div`display: flex; align-items: center; gap: ${theme.spacing[2]}; font-size: ${theme.typography.base}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary}; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`;
const EditorTextarea = styled.textarea`flex: 1; padding: ${theme.spacing[5]}; border: none; outline: none; resize: none; font-family: 'JetBrains Mono', 'Fira Code', monospace; font-size: 13px; line-height: 1.6; color: ${theme.colors.textPrimary}; background: ${theme.colors.gray50 || '#f9fafb'};`;
const EditorLoadingState = styled.div`flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: ${theme.spacing[3]}; color: ${theme.colors.textMuted}; font-size: ${theme.typography.sm};`;

export default KnowledgeBase;
